import { useMemo } from "react";
import moment from "moment-timezone";
import { Type } from "@themes/pollinator/ui/Type";
import { Link } from "react-router-dom";
import { Settings, Video, Edit } from "lucide-react";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { DevicePermissionInitializer } from "@platform/features/video/components/DevicePermissionInitializer";
import SpeakerDevices from "@platform/features/video/components/DeviceSelection/SpeakerDevices";
import MicSelection from "@platform/features/video/components/DeviceSelection/MicrophoneDevices/MicSelection";
import MicrophoneActivityPreview from "@platform/features/video/components/DeviceSelection/MicrophoneDevices/MicrophoneActivityPreview";
import { CameraSelection } from "@platform/features/video/components/DeviceSelection/CameraDevices/CameraSelection";
import MeetingJoinDetails from "@platform/features/video/containers/MeetingJoinDetails";
import { MeetingDetails } from "@platform/features/video/types/video.types";
import { PreviewVideo } from "@platform/features/video/components/DeviceSelection/CameraDevices/PreviewVideo";
import { VideoUIRosterItem } from "../VideoUIRoster/VideoUIRosterItem";
import useUserFullname from "@platform/hooks/useUserFullname";
import { Button } from "@themes/pollinator/ui/Button";
import { useVideo } from "@platform/features/video/providers/VideoContext";
import { extractRoomMemberData } from "@platform/features/video/utils/roomMemberDataUtils";
import VideoUIRoleAndDescriptionContainer from "../VideoUIRoleAndDescription/VideoUIRoleAndDescriptionContainer";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";
import VideoUIPermissionsTip from "../VideoUIPermissionsTip";
import VideoUIPermissionsGuide from "../VideoUIPermissionsGuide";

export type Props = {
  meetingDetails: MeetingDetails | null;
  meetingStartTime?: string;
  isDevicesLoading: boolean;
  onJoinMeeting: () => Promise<void>;
  isActionLoading: boolean;
};

export function VideoUIJoin({
  meetingDetails,
  meetingStartTime,
  isDevicesLoading,
  onJoinMeeting,
  isActionLoading,
}: Props) {
  if (!meetingDetails) {
    return null;
  }

  const userFullname = useUserFullname();

  const { audioInputDevices, videoInputDevices } = useDevices();

  const { userName, roomMembersData } = useVideo();

  // Extract room member data for the current user
  const { roomMemberData } = userName
    ? extractRoomMemberData({ externalUserId: userName }, roomMembersData)
    : { roomMemberData: null };

  // Check if we need to show the permission request button
  // Show if we don't have devices AND we're not currently loading
  const needsPermissionRequest =
    !isDevicesLoading &&
    (audioInputDevices.length === 0 || videoInputDevices.length === 0);

  // Check if user is on iOS Chrome - multiple detection methods
  const userAgent = navigator.userAgent;
  const isIOSChrome =
    /CriOS/i.test(userAgent) ||
    (/iPhone|iPad|iPod/i.test(userAgent) && /Chrome/i.test(userAgent));

  // Check if we should show the iOS Chrome disclaimer
  // Show it if it's iOS Chrome and either we need permissions OR we're still loading (in case it gets stuck)
  const showIOSChromeDisclaimer =
    isIOSChrome && (needsPermissionRequest || isDevicesLoading);

  return (
    <>
      <OuterContentContainer
        className="flex flex-1 md:py-0 md:min-h-[calc(100vh-71px)] md:min-h-[calc(100dvh-71px)]"
        innerClassName="flex-1 flex-col-reverse px-4 py-8 md:flex-col md:px-0 md:py-0 md:flex-row md:h-full md:items-stretch md:justify-between"
      >
        <div className="flex flex-col gap-8 w-full px-0 md:max-w-sm md:py-8 md:px-8">
          <div className="flex flex-col gap-2">
            <Type as="h2" variant="h4" className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              Video Preview
            </Type>
            <PreviewVideo />
          </div>

          <div className="flex flex-col gap-4">
            <Type as="h2" variant="h4" className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Device Settings
            </Type>
            <Type variant="caption" className="text-brand-700">
              You can join the meeting even if you don't have a camera
              available.
            </Type>
            <DevicePermissionInitializer />
            <CameraSelection />
            <MicSelection />
            <MicrophoneActivityPreview />
            <SpeakerDevices />
          </div>
          <div className="flex flex-col gap-2 md:hidden">
            <Type
              as="h3"
              variant="h6"
              className="flex items-center gap-2 text-brand-700"
            >
              Ready to Join?
            </Type>
            <MeetingJoinDetails
              meetingId={meetingDetails.id.toString()}
              isDevicesLoading={isDevicesLoading}
              isActionLoading={isActionLoading}
              onJoinMeeting={onJoinMeeting}
            />
          </div>
        </div>
        <div className="flex flex-col flex-1 pt-0 pb-16 w-full gap-8 border-brand-800 md:py-8 md:border-l md:px-8 md:items-center">
          <div className="flex flex-col gap-8 w-full sm:gap-16 md:gap-32 md:max-w-3xl">
            <AppPageHeader
              title={meetingDetails.title}
              description={`Scheduled for ${meetingStartTime}`}
              titleTypeVariant="h2"
              className="md:max-w-3xl"
            />
            <div className="flex flex-col-reverse gap-8 w-full md:max-w-lg md:gap-12 md:flex-col">
              {showIOSChromeDisclaimer && (
                <div className="flex flex-col gap-2">
                  <Type as="h2" variant="h4" className="text-error-700">
                    iOS Chrome Detected
                  </Type>
                  <div className="p-4 border border-error-700 rounded-md flex flex-col gap-2">
                    <Type variant="caption" className="text-error-700">
                      You may need to turn on microphone access in settings via
                      Settings → Privacy & Security → Microphone Settings
                      (enable Chrome). Alternatively, you can use iOS Safari for
                      better compatibility.
                    </Type>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Type as="h2" variant="h4" className="flex items-center gap-2">
                  How You'll Appear to Others
                </Type>
                <div className="flex flex-col gap-2 items-start">
                  <div className="border border-brand-800 rounded-md p-4 shadow-md w-full">
                    <VideoUIRosterItem
                      muted={false}
                      videoEnabled={true}
                      sharingContent={false}
                      attendeeName={userFullname}
                      roomMemberData={roomMemberData}
                    />
                  </div>
                  <Button
                    as={Link}
                    variant="link"
                    iconComponent={Edit}
                    to="/account"
                  >
                    Edit Profile
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-8 border border-brand-800 rounded-md p-4 shadow-md bg-brand-100">
                  <VideoUIRoleAndDescriptionContainer
                    meetingDetails={meetingDetails}
                  />
                  <MeetingJoinDetails
                    meetingId={meetingDetails.id.toString()}
                    isDevicesLoading={isDevicesLoading}
                    isActionLoading={isActionLoading}
                    onJoinMeeting={onJoinMeeting}
                  />
                </div>
                <div className="flex flex-col gap-0 items-start lg:flex-row lg:gap-2 lg:items-center">
                  <Type
                    as="h2"
                    variant="h6"
                    className="flex items-center gap-2 text-error-700"
                  >
                    Having issues with your camera or microphone?
                  </Type>
                  <VideoUIPermissionsTip />
                </div>
              </div>
            </div>
          </div>
        </div>
      </OuterContentContainer>

      {/* Permissions guide dialog */}
      <VideoUIPermissionsGuide />
    </>
  );
}

export default VideoUIJoin;
